// Project-owned login orchestration; reuse shared controls and styles without modifying common.
/* eslint-disable react/jsx-indent */
/* eslint-disable indent */
import { prepareLogin } from '@/security/session';
import { loginRememberKey, sendCodeTimeKey } from '@/config/base';
import { randomLenNum } from '@/utils';
import { removeLocalStorage, removeToken, setLocalStorage, setToken } from '@/utils/cookies';
import { QrcodeOutlined } from '@ant-design/icons';
import { ConfigProvider, Spin, message } from '@sinohealth/butterfly-ui-components';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AccountPasswordLogin from '@/common/components/BasicLogin/components/AccountPasswordLogin';
import BindMobile from '@/common/components/BasicLogin/components/BindMobile';
import ForgetPassword from './ForgetPassword';
import ScanCode from '@/common/components/BasicLogin/components/ScanCode';
import SelectOrganization from '@/common/components/BasicLogin/components/SelectOrganization';
import SmsLogin from '@/common/components/BasicLogin/components/SmsLogin';
import Tabs from '@/common/components/BasicLogin/components/Tabs';
import {
  doLogin,
  httpAddWechatPassport,
  httpListUserOrganize,
  switchChain,
} from '@/common/components/BasicLogin/http';
import styles from '@/common/components/BasicLogin/index.less';

export interface TabsListProps {
  label: string;
  code: 'account' | 'msm' | 'forget' | 'qrCode' | 'workQrCode' | 'bindPhone';
}

type BasicLoginProps = {
  /** 选择机构的title */
  organizaTitle?: string;
  /** 平台名称 */
  platformName?: string;
  /** 白色背景 */
  white?: boolean;
  /** 是否需要 二维码登录 */
  qrcodeLogin?: boolean;
  /** 默认登录 */
  defaultLogin?: boolean;
  /** 主题颜色 */
  colorPrimary?: string;
  /** 选择机构弹窗 */
  chooseOrganization?: boolean;
  /** 头部tab { label: string; code: 'account' | 'msm' | 'forget' | 'qrCode' | 'bindPhone' }[] */
  tabsLis?: TabsListProps[];
  /** 用户服务名称 */
  serviceText?: string;
  /** 隐私协议名称 */
  privacyText?: string;
  /** 服务协议 */
  serviceProtocolUrl?: string;
  /** 隐私协议 */
  privacyProtocolUrl?: string;
  /** 是否展示协议 */
  protocolBool?: boolean;
  /** 机构列表-非默认登录时必填 */
  source?: { id: string; name: string }[];
  /** 是否需要选择机构 */
  organizeBol?: boolean;
  /** 登录成功回调 */
  onSuccess?: (val: any, type?: string, bol?: boolean) => void;
  /** 取消选择机构时回调 */
  onCancel?: (token?: string) => void;
  /** 选择机构回调 */
  onOk?: (token?: string) => void;
};

/** */
const BasicLogin: React.FC<BasicLoginProps> = (props) => {
  const {
    white,
    organizaTitle,
    platformName,
    qrcodeLogin,
    defaultLogin,
    colorPrimary = '#217ba0',
    organizeBol = true,
    serviceProtocolUrl,
    privacyProtocolUrl,
    serviceText,
    privacyText,
    protocolBool,
    chooseOrganization = false,
    tabsLis,
    source,
    onSuccess,
    onCancel,
    onOk,
  } = props;

  const navigate = useNavigate();
  const [urlParams] = useSearchParams();
  const [page] = urlParams.getAll('tab');
  const [accessToken] = urlParams.getAll('token');
  const [appId] = urlParams.getAll('appId');
  const [openId] = urlParams.getAll('openId');
  const [unionId] = urlParams.getAll('unionId');
  const [channel] = urlParams.getAll('channel');
  const [errMessage] = urlParams.getAll('errMessage');
  // 企业微信绑定手机号码机构id
  const [organizeId] = urlParams.getAll('organizeId');

  const [currentTabs, setCurrentTabs] = useState<
    'account' | 'msm' | 'forget' | 'qrCode' | 'error' | 'workQrCode' | 'bindPhone' | string
  >(page || 'account'); // account msm forget qrCode bindPhone
  const [chooseModal, setChooseModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [organizeList, setOrganizeList] = useState<any>([]);
  const [successToken, setSuccessToken] = useState('');
  const [randomNum, setRandomNum] = useState(randomLenNum(4, true));

  /** 获取机构 */
  const httpgetListOrganizeReq = async (params?: any, token?: any) => {
    try {
      const list = await httpListUserOrganize({ token });

      setOrganizeList(list);
      setIsLoading(false);
      setChooseModal(true);
    } catch {
      setIsLoading(false);
    }
  };

  /** 登录 */
  const httpDoLoginReq = async (params: any, ref?: any, type = 'account', bol = true) => {
    const copyParams = type === 'account' ? { seq: randomNum, ...params } : params;

    try {
      setIsLoading(true);
      const token: any = await doLogin(copyParams);
      await prepareLogin(token);
      if (type === 'account') {
        bol
          ? setLocalStorage(loginRememberKey, {
              account: copyParams[type],
              pwd: copyParams.pwd,
            })
          : removeLocalStorage(loginRememberKey);
      }

      setSuccessToken(token);

      // 扫码登录-需要绑定手机
      if (currentTabs === 'bindPhone') {
        try {
          await httpAddWechatPassport({ appId, channel, unionId, openId }, { token });

          // 企业微信不需要选择机构
          if (channel === 'wxWork') {
            httpSwitchChainReq(organizeId, token);
            return;
          }
          // 需要机构
          needOranizeLogin({ channel: copyParams.channel, credentials: copyParams[type] }, token);
        } catch (err) {
          setIsLoading(false);
          return;
        }
      } else {
        // 企业微信不需要选择机构
        if (channel === 'wxWork') {
          handleNavigateTo(token);
          return;
        }
        // 需要机构
        needOranizeLogin({ channel: copyParams.channel, credentials: copyParams[type] }, token);
      }
    } catch {
      setRandomNum(randomLenNum(4, true));
      removeToken();
      ref && ref?.current?.refreshFn();
      setIsLoading(false);
    }
  };

  // 企业微信授权-切换机构
  const httpSwitchChainReq = async (v: string, token: string) => {
    try {
      await switchChain(v, { token });
      handleNavigateTo(token);
    } finally {
      setIsLoading(false);
    }
  };

  // 需要切换机构
  const needOranizeLogin = (params?: any, token?: string) => {
    // 需要机构
    if (organizeBol) {
      httpgetListOrganizeReq({}, token);
    } else {
      handleNavigateTo(token);
    }
  };

  // 登录成功跳转页面
  const handleNavigateTo = (t?: string) => {
    setToken(t || '');
    setIsLoading(false);
    removeLocalStorage(sendCodeTimeKey);
    onOk && onOk();
    navigate('/');
  };

  useEffect(() => {
    if (!defaultLogin) {
      setChooseModal(chooseOrganization);
      setOrganizeList(source);
    }
  }, [defaultLogin, source, chooseOrganization]);

  useEffect(() => {
    // 登录页默认清楚Token
    removeToken();
    // 扫码登录时,有Token就跳转页面
    if (!accessToken) return undefined;
    let active = true;
    setIsLoading(true);
    prepareLogin(accessToken)
      .then(() => {
        if (!active) return;
        setSuccessToken(accessToken);
        if (channel === 'wxWork') {
          handleNavigateTo(accessToken);
        } else {
          needOranizeLogin({}, accessToken);
        }
      })
      .catch(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [accessToken]);

  useEffect(() => {
    if (errMessage) {
      message.error({ content: errMessage });
      window.location.hash = '/login';
    }
  }, [errMessage]);

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary,
        },
      }}
    >
      <Spin spinning={isLoading} tip="登录中, 请稍候">
        <div className={styles.basicLogin}>
          {qrcodeLogin && !chooseModal && (currentTabs === 'account' || currentTabs === 'msm') ? (
            <div className={styles['scan-code']} onClick={() => setCurrentTabs('qrCode')}>
              <QrcodeOutlined style={{ fontSize: 40, color: colorPrimary }} />
            </div>
          ) : null}
          {!chooseModal && (currentTabs === 'account' || currentTabs === 'msm') ? (
            <Tabs tabs={tabsLis} current={currentTabs} onChange={setCurrentTabs} />
          ) : null}
          <div className={styles.container}>
            {/* 账号密码登录 */}
            {!chooseModal && currentTabs === 'account' ? (
              <AccountPasswordLogin
                white={white}
                platformName={platformName}
                privacyProtocolUrl={privacyProtocolUrl}
                protocolBool={protocolBool}
                serviceText={serviceText}
                privacyText={privacyText}
                serviceProtocolUrl={serviceProtocolUrl}
                forgetUrl={() => setCurrentTabs('forget')}
                onLogin={(val, bol, ref) =>
                  defaultLogin
                    ? httpDoLoginReq(val, ref, 'account', bol)
                    : onSuccess?.(val, 'account', bol)
                }
              />
            ) : null}
            {/* 手机号码登录 */}
            {!chooseModal && currentTabs === 'msm' ? (
              <SmsLogin
                white={white}
                platformName={platformName}
                privacyProtocolUrl={privacyProtocolUrl}
                protocolBool={protocolBool}
                serviceText={serviceText}
                privacyText={privacyText}
                serviceProtocolUrl={serviceProtocolUrl}
                onLogin={(val: any) =>
                  defaultLogin
                    ? httpDoLoginReq(val, '', 'phone', false)
                    : onSuccess?.(val, 'phone', false)
                }
              />
            ) : null}
            {/* 忘记密码 */}
            {!chooseModal && currentTabs === 'forget' ? (
              <ForgetPassword
                white={white}
                backLogin={() => {
                  setCurrentTabs('account');
                }}
              />
            ) : null}
            {/* 扫码登录 */}
            {!chooseModal &&
            (currentTabs === 'qrCode' ||
              currentTabs === 'workQrCode' ||
              currentTabs === 'error') ? (
              <ScanCode
                privacyProtocolUrl={privacyProtocolUrl}
                platformName={platformName}
                serviceText={serviceText}
                privacyText={privacyText}
                serviceProtocolUrl={serviceProtocolUrl}
                onAccount={() => {
                  setCurrentTabs('account');
                }}
                onMobile={() => {
                  setCurrentTabs('msm');
                }}
              />
            ) : null}
            {/* 绑定手机号码 */}
            {!chooseModal && currentTabs === 'bindPhone' ? (
              <BindMobile
                privacyProtocolUrl={privacyProtocolUrl}
                protocolBool={protocolBool}
                serviceText={serviceText}
                privacyText={privacyText}
                serviceProtocolUrl={serviceProtocolUrl}
                onBack={() => setCurrentTabs('qrCode')}
                onLogin={(val: any) =>
                  defaultLogin
                    ? httpDoLoginReq(val, '', 'phone', false)
                    : onSuccess?.(val, 'phone', false)
                }
              />
            ) : null}
            {page !== 'workQrCode' && organizeBol && chooseModal ? (
              <SelectOrganization
                organizaTitle={organizaTitle}
                source={organizeList}
                white={white}
                token={successToken}
                onCancel={() => {
                  defaultLogin ? setChooseModal(false) : onCancel?.(successToken);
                  setSuccessToken('');
                  removeToken();
                  // 当地址栏tab=bindPhone，organizeBol = true 时，返回扫码
                  if ((page === 'bindPhone' || currentTabs === 'qrCode') && organizeBol) {
                    setCurrentTabs('qrCode');
                  } else {
                    setCurrentTabs('account');
                  }
                  window.location.hash = '/login';
                }}
                onOk={() => {
                  defaultLogin ? setChooseModal(false) : onOk?.(successToken);
                  setToken(successToken);
                  onOk?.();
                }}
              />
            ) : null}
          </div>
        </div>
      </Spin>
    </ConfigProvider>
  );
};

export default BasicLogin;
